import { POSTS_PER_FILE } from './post-engine';
import { getMode } from './auth';

const STORAGE_KEY = 'downread-postcounts';
const CACHE_MAX_AGE_MS = 86400000 * 30; // 30 days

interface PostCountEntry {
  postCount: number;
  timestamp: number;
}

interface PostCountCache {
  [feedId: string]: PostCountEntry;
}

/**
 * Engine for tracking feed post counts across sessions.
 * Needed to determine if posts are archived when the user is no longer subscribed to a feed.
 */
class PostCountEngine {
  private cache: PostCountCache = {};
  private pendingFetches = new Map<string, Promise<number>>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.cache = JSON.parse(stored);
        // Clean up old entries
        const now = Date.now();
        for (const [feedId, entry] of Object.entries(this.cache)) {
          if (now - entry.timestamp > CACHE_MAX_AGE_MS) {
            delete this.cache[feedId];
          }
        }
      }
    } catch (e) {
      console.error('Failed to load postcounts from storage:', e);
      this.cache = {};
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.cache));
    } catch (e) {
      console.error('Failed to save postcounts to storage:', e);
    }
  }

  /**
   * Update post counts for multiple feeds (called from feed-engine after polls/updates)
   */
  updatePostCounts(postCounts: Record<string, number>) {
    const now = Date.now();
    let changed = false;
    for (const [feedId, postCount] of Object.entries(postCounts)) {
      if (!this.cache[feedId] || this.cache[feedId].postCount < postCount) {
        this.cache[feedId] = { postCount, timestamp: now };
        changed = true;
      }
    }
    if (changed) {
      this.saveToStorage();
    }
  }

  /**
   * Get post count for a feed (from cache or fetch from API)
   */
  async getPostCount(feedId: string): Promise<number> {
    // Check cache first
    const cached = this.cache[feedId];
    if (cached) {
      return cached.postCount;
    }

    // Check if already fetching
    const pending = this.pendingFetches.get(feedId);
    if (pending) {
      return pending;
    }

    // Fetch from API
    const fetchPromise = this.fetchPostCount(feedId);
    this.pendingFetches.set(feedId, fetchPromise);

    try {
      const postCount = await fetchPromise;
      this.pendingFetches.delete(feedId);

      // Store in cache
      this.cache[feedId] = { postCount, timestamp: Date.now() };
      this.saveToStorage();

      return postCount;
    } catch (e) {
      this.pendingFetches.delete(feedId);
      throw e;
    }
  }

  private async fetchPostCount(feedId: string): Promise<number> {
    // Anonymous sessions have no authenticated /api/feed access; use the public endpoint instead
    // (this fallback only fires for feeds not already seeded from the feeds load / poll).
    const path = getMode() == 'anon'
      ? `/api/public/feed/${encodeURIComponent(feedId)}`
      : `/api/feed/${feedId}`;
    const response = await fetch(path, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch feed ${feedId}: ${response.status}`);
    }

    const feed = await response.json();
    return feed.postCount || 0;
  }

  /**
   * Check if a post index is archived (vs in current.dz)
   * Uses optimization: if index is far enough behind known postCount, we can answer without fetching
   */
  async isPostIndexArchived(feedId: string, index: number): Promise<boolean> {
    // Check if we have a cached postCount
    const cached = this.cache[feedId];

    if (cached) {
      // Optimization: If the post is at least POSTS_PER_FILE behind the known postCount,
      // it's definitely archived (even if postCount has increased since)
      // This is because the archive boundary only moves forward
      if (index < cached.postCount - POSTS_PER_FILE) {
        return true;
      }
    }

    // Need to fetch current postCount to be sure
    const postCount = await this.getPostCount(feedId);

    // Use the same formula as isArchived() in post-engine.ts
    const archiveBoundary = postCount - (postCount % POSTS_PER_FILE || POSTS_PER_FILE);
    return index < archiveBoundary;
  }

  /**
   * Get cached post count without fetching (returns undefined if not cached)
   */
  getCachedPostCount(feedId: string): number | undefined {
    return this.cache[feedId]?.postCount;
  }
}

// Singleton instance
export const postCountEngine = new PostCountEngine();
